import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { SETTLEMENT_TYPE } from "@/schemas/commonFields";
import { AGREEMENT_STATUS } from "@/types/agreement";
import { TFunction } from "i18next";
import { ColumnDef } from "@tanstack/react-table";
import { FilePreviewLink } from "@/components/FilePreviewLink";
import { TAX_STATUS } from "@/schemas/commonFields/taxStatus";

export const createAgreementStatusPeriod = <T extends object>(
  t: TFunction,
): ColumnDef<T> => ({
  id: "agreement_status_period",
  meta: {
    filter: {
      component: EnumFilter,
      props: {
        options: AGREEMENT_STATUS,
        translationKey: "agreementStatuses",
      },
    },
  },
  cell: ({ row }) => {
    if (
      "agreement_status_period" in row.original &&
      row.original.agreement_status_period
    ) {
      return t(`agreementStatuses.${row.original.agreement_status_period}`);
    }
  },
});

export const createAgreementStatusCurrent = <T extends object>(
  t: TFunction,
): ColumnDef<T> => ({
  id: "agreement_status_current",
  meta: {
    filter: {
      component: EnumFilter,
      props: {
        options: AGREEMENT_STATUS,
        translationKey: "agreementStatuses",
      },
    },
  },
  cell: ({ row }) => {
    if (
      "agreement_status_current" in row.original &&
      row.original.agreement_status_current
    ) {
      return t(`agreementStatuses.${row.original.agreement_status_current}`);
    }
  },
});

export const createSettlementType = <T extends object>(
  t: TFunction,
): ColumnDef<T> => ({
  id: "settlement_type",
  cell: ({ row }) => {
    if ("settlement_type" in row.original) {
      return t(`settlementType.${row.original.settlement_type}`);
    }
  },
  meta: {
    filter: {
      component: EnumFilter,
      props: {
        options: SETTLEMENT_TYPE,
        translationKey: "settlementType",
      },
    },
  },
});

export const createTaxStatusPeriod = <T extends Record<string, unknown>>(
  t: TFunction,
): ColumnDef<T> => ({
  id: "tax_status_period",
  cell: ({ row }) => {
    if ("tax_status_period" in row.original) {
      const value = row.original.tax_status_period;
      return t(`taxStatus.${value}`);
    }
  },
  meta: {
    filter: {
      component: EnumFilter,
      props: {
        options: TAX_STATUS,
        translationKey: "taxStatus",
      },
    },
  },
});

export const createTaxStatusCurrent = <T extends Record<string, unknown>>(
  t: TFunction,
): ColumnDef<T> => ({
  id: "tax_status_current",
  cell: ({ row }) => {
    if ("tax_status_current" in row.original) {
      const value = row.original.tax_status_current;
      return t(`taxStatus.${value}`);
    }
  },
  meta: {
    filter: {
      component: EnumFilter,
      props: {
        options: TAX_STATUS,
        translationKey: "taxStatus",
      },
    },
  },
});

export const createTaxStatusCorrected = <T extends Record<string, unknown>>(
  t: TFunction,
): ColumnDef<T> => ({
  id: "tax_status_corrected",
  cell: ({ row }) => {
    if (
      "tax_status_corrected" in row.original &&
      row.original.tax_status_corrected
    ) {
      return t(`taxStatus.${row.original.tax_status_corrected}`);
    }
  },
  meta: {
    filter: {
      component: EnumFilter,
      props: {
        options: TAX_STATUS,
        translationKey: "taxStatus",
      },
    },
  },
});

export const createTaxStatusFinal = <T extends Record<string, unknown>>(
  t: TFunction,
): ColumnDef<T> => ({
  id: "tax_status_final",
  cell: ({ row }) => {
    if ("tax_status_final" in row.original) {
      return t(`taxStatus.${row.original.tax_status_final}`);
    }
  },
  meta: {
    filter: {
      component: EnumFilter,
      props: {
        options: TAX_STATUS,
        translationKey: "taxStatus",
      },
    },
  },
});

export const createContractorCardS3Key = <
  T extends object,
>(): ColumnDef<T> => ({
  id: "contractor_card_s3_key",
  cell: ({ row }) => {
    const data = row.original;
    if (
      "contractor_card_s3_key" in data &&
      "contractor_card_filename" in data
    ) {
      return (
        <FilePreviewLink
          s3Key={data.contractor_card_s3_key as string}
          displayFileName={data.contractor_card_filename as string}
        />
      );
    }
    return null;
  },
  enableColumnFilter: false,
  enableSorting: false,
  meta: {
    allowColumnToBeALink: false,
  },
});
